import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Timeline as TimelineIcon,
  Security as SecurityIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  FolderOpen as CaseIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { formatDateTime } from '../utils/dateUtils';

const EMPTY_OPTIONS = { statuses: [], severities: [], categories: [], sources: [] };

const formFromDetection = (d) => ({
  status: d?.status || '',
  assignedTo: d?.assignedTo?._id || (typeof d?.assignedTo === 'string' ? d.assignedTo : '') || '',
  severity: d?.severity || '',
  category: d?.category || '',
  subcategory: d?.subcategory || '',
  case: d?.case?._id || (typeof d?.case === 'string' ? d.case : '') || ''
});

export const DetectionDetail = ({ open, onClose, detectionId }) => {
  const { user } = useAuth();
  const { getSeverityColor: settingsSeverityColor, getStatusColor: settingsStatusColor } = useSettings();
  const [detection, setDetection] = useState(null);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Case management state
  const [editingCaseManagement, setEditingCaseManagement] = useState(false);
  const [caseManagementForm, setCaseManagementForm] = useState({
    status: '',
    assignedTo: '',
    severity: '',
    category: '',
    subcategory: '',
    case: ''
  });
  const [savingCaseManagement, setSavingCaseManagement] = useState(false);
  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);

  const fetchDetection = useCallback(async () => {
    if (!detectionId) return;

    try {
      setLoading(true);
      setError('');
      const response = await apiService.detections.getById(detectionId);
      setDetection(response.data.detection);

      // Initialize case management form with current values
      setCaseManagementForm(formFromDetection(response.data.detection));
    } catch (err) {
      console.error('Error fetching detection:', err);
      setError('Failed to load detection details');
    } finally {
      setLoading(false);
    }
  }, [detectionId]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiService.users.getAll({ limit: 100 });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchCases = useCallback(async () => {
    try {
      const response = await apiService.cases.getAll({ limit: 100 });
      setCases(response.data.cases || []);
    } catch (err) {
      console.error('Error fetching cases:', err);
    }
  }, []);

  useEffect(() => {
    if (open && detectionId) {
      fetchDetection();
      fetchUsers();
      fetchCases();
    }
  }, [open, detectionId, fetchDetection, fetchUsers, fetchCases]);

  useEffect(() => {
    if (!open) return;
    apiService.detections.getOptions()
      .then((res) => setOptions({ ...EMPTY_OPTIONS, ...res.data }))
      .catch((err) => console.error('Error loading detection options:', err));
  }, [open]);

  const handleEditCaseManagement = () => {
    setEditingCaseManagement(true);
  };

  const handleCancelCaseManagement = () => {
    setEditingCaseManagement(false);
    // Reset form to current detection values
    setCaseManagementForm(formFromDetection(detection));
  };

  /**
   * Save case-management fields. `overrides` are merged over the current form so
   * quick actions can change a field and save in one step (no stale closure).
   * Only fields that differ from the loaded detection are sent.
   */
  const handleSaveCaseManagement = async (overrides = {}) => {
    if (!detection) return;
    const form = { ...caseManagementForm, ...overrides };
    const current = formFromDetection(detection);
    const updateData = {};
    ['status', 'severity', 'category'].forEach((key) => {
      if (form[key] && form[key] !== current[key]) updateData[key] = form[key];
    });
    if (form.assignedTo !== current.assignedTo) updateData.assignedTo = form.assignedTo || null;
    if ((form.subcategory || '').trim() !== current.subcategory) updateData.subcategory = (form.subcategory || '').trim() || null;
    if (form.case !== current.case) updateData.case = form.case || null;

    if (Object.keys(updateData).length === 0) {
      setEditingCaseManagement(false);
      return;
    }

    try {
      setSavingCaseManagement(true);
      setError('');
      await apiService.detections.update(detectionId, updateData);
      await fetchDetection(); // Refresh detection data
      setEditingCaseManagement(false);
      setSuccessMessage('Detection updated');
    } catch (err) {
      console.error('Error updating detection:', err);
      setError(err.response?.data?.message || 'Failed to update detection');
    } finally {
      setSavingCaseManagement(false);
    }
  };

  const handleCaseManagementFormChange = (field, value) => {
    setCaseManagementForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateCaseFromDetection = async () => {
    try {
      const caseData = {
        title: `Case for Detection: ${detection.title}`.slice(0, 200),
        description: `Case created from detection: ${detection.title}\n\nDetection Description: ${detection.description}`.slice(0, 2000),
        priority: detection.severity,
        detectionIds: [detectionId]
      };

      const response = await apiService.detections.createCase(caseData);

      // Update the detection to link it to the new case
      await fetchDetection();

      setError('');
      setSuccessMessage(`Case ${response.data.case?.caseNumber || ''} created from this detection`.replace('  ', ' '));
    } catch (err) {
      console.error('Error creating case:', err);
      setError(err.response?.data?.message || 'Failed to create case from detection');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setAddingComment(true);
      await apiService.detections.addComment(detectionId, {
        content: newComment.trim()
      });
      setNewComment('');
      // Refresh detection to get updated comments
      await fetchDetection();
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err.response?.data?.message || 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const formatDetectionDateTime = (dateString) => {
    return formatDateTime(dateString, user?.preferences);
  };

  const severityStyle = (severity) => {
    const color = settingsSeverityColor(severity) || {};
    return { backgroundColor: color.main || '#757575', color: color.contrastText || '#fff' };
  };

  const statusStyle = (status) => {
    const c = settingsStatusColor(status);
    const color = (typeof c === 'string' ? c : c?.main) || '#757575';
    return { borderColor: color, color };
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">Detection Details</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : error && !detection ? (
          <Box p={3}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : detection ? (
          <Box>
            {error && (
              <Box px={3} pt={2}>
                <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
              </Box>
            )}
            {successMessage && (
              <Box px={3} pt={2}>
                <Alert severity="success" onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
              </Box>
            )}
            {/* Header Information */}
            <Box p={3} bgcolor="grey.50">
              <Typography variant="h5" gutterBottom>
                {detection.title}
              </Typography>
              <Box display="flex" gap={1} mb={2}>
                <Chip
                  label={detection.severity}
                  size="small"
                  sx={severityStyle(detection.severity)}
                />
                <Chip
                  label={detection.status}
                  size="small"
                  variant="outlined"
                  sx={statusStyle(detection.status)}
                />
                <Chip
                  label={detection.category}
                  variant="outlined"
                  size="small"
                />
                {detection.subcategory && (
                  <Chip
                    label={detection.subcategory}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Source:</strong> {detection.source || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Source ID:</strong> {detection.sourceId || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Detection Time:</strong> {formatDetectionDateTime(detection.detectionTime)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Created By:</strong> {detection.createdBy ? `${detection.createdBy.firstName} ${detection.createdBy.lastName}` : 'System'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Assigned To:</strong> {detection.assignedTo ? `${detection.assignedTo.firstName} ${detection.assignedTo.lastName}` : 'Unassigned'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Created:</strong> {formatDateTime(detection.createdAt)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box p={3}>
              {/* Description */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {detection.description}
                  </Typography>
                </CardContent>
              </Card>

              {/* Case Management */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                      <AssignmentIcon color="primary" />
                      Case Management
                    </Typography>
                    {!editingCaseManagement ? (
                      <Button
                        startIcon={<EditIcon />}
                        onClick={handleEditCaseManagement}
                        size="small"
                        variant="outlined"
                      >
                        Edit
                      </Button>
                    ) : (
                      <Box display="flex" gap={1}>
                        <Button
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveCaseManagement()}
                          size="small"
                          variant="contained"
                          disabled={savingCaseManagement}
                        >
                          {savingCaseManagement ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          startIcon={<CancelIcon />}
                          onClick={handleCancelCaseManagement}
                          size="small"
                          variant="outlined"
                          disabled={savingCaseManagement}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {!editingCaseManagement ? (
                    // Display mode
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Status:</strong>
                        </Typography>
                        <Chip
                          label={detection.status}
                          size="small"
                          variant="outlined"
                          sx={{ mb: 2, ...statusStyle(detection.status) }}
                        />

                        <Typography variant="body2" gutterBottom>
                          <strong>Severity:</strong>
                        </Typography>
                        <Chip
                          label={detection.severity}
                          size="small"
                          sx={{ mb: 2, ...severityStyle(detection.severity) }}
                        />

                        <Typography variant="body2" gutterBottom>
                          <strong>Category:</strong>
                        </Typography>
                        <Chip
                          label={detection.category}
                          variant="outlined"
                          size="small"
                          sx={{ mb: 2 }}
                        />

                        {detection.subcategory && (
                          <>
                            <Typography variant="body2" gutterBottom>
                              <strong>Subcategory:</strong>
                            </Typography>
                            <Chip
                              label={detection.subcategory}
                              variant="outlined"
                              size="small"
                              sx={{ mb: 2 }}
                            />
                          </>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Assigned To:</strong>
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <PersonIcon fontSize="small" />
                          <Typography variant="body2">
                            {detection.assignedTo ?
                              `${detection.assignedTo.firstName} ${detection.assignedTo.lastName}` :
                              'Unassigned'
                            }
                          </Typography>
                        </Box>

                        <Typography variant="body2" gutterBottom>
                          <strong>Associated Case:</strong>
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <CaseIcon fontSize="small" />
                          <Typography variant="body2">
                            {detection.case ?
                              `${detection.case.caseNumber} - ${detection.case.title}` :
                              'No case assigned'
                            }
                          </Typography>
                        </Box>

                        {!detection.case && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CaseIcon />}
                            onClick={handleCreateCaseFromDetection}
                            sx={{ mt: 1 }}
                          >
                            Create Case
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  ) : (
                    // Edit mode
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth margin="normal" size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={caseManagementForm.status}
                            onChange={(e) => handleCaseManagementFormChange('status', e.target.value)}
                            label="Status"
                          >
                            {options.statuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth margin="normal" size="small">
                          <InputLabel>Severity</InputLabel>
                          <Select
                            value={caseManagementForm.severity}
                            onChange={(e) => handleCaseManagementFormChange('severity', e.target.value)}
                            label="Severity"
                          >
                            {options.severities.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth margin="normal" size="small">
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={caseManagementForm.category}
                            onChange={(e) => handleCaseManagementFormChange('category', e.target.value)}
                            label="Category"
                          >
                            {(options.categories.includes(caseManagementForm.category) || !caseManagementForm.category
                              ? options.categories
                              : [caseManagementForm.category, ...options.categories]
                            ).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={users}
                          getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.username})`}
                          value={users.find(u => u._id === caseManagementForm.assignedTo) || null}
                          onChange={(event, newValue) => {
                            handleCaseManagementFormChange('assignedTo', newValue?._id || '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Assigned To"
                              margin="normal"
                              size="small"
                              fullWidth
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Avatar sx={{ mr: 2, width: 24, height: 24 }}>
                                {option.firstName?.[0]}{option.lastName?.[0]}
                              </Avatar>
                              {option.firstName} {option.lastName} ({option.username})
                            </Box>
                          )}
                        />

                        <Autocomplete
                          options={cases}
                          getOptionLabel={(option) => `${option.caseNumber} - ${option.title}`}
                          value={cases.find(c => c._id === caseManagementForm.case) || null}
                          onChange={(event, newValue) => {
                            handleCaseManagementFormChange('case', newValue?._id || '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Associated Case"
                              margin="normal"
                              size="small"
                              fullWidth
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <CaseIcon sx={{ mr: 2 }} />
                              {option.caseNumber} - {option.title}
                            </Box>
                          )}
                        />
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>

              {/* AI Analysis */}
              {detection.aiAnalysis && (
                <Accordion defaultExpanded sx={{ mb: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">AI Analysis</Typography>
                    <Chip
                      label={`${detection.aiAnalysis.confidence}% confidence`}
                      size="small"
                      color="info"
                      sx={{ ml: 2 }}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography variant="body1" paragraph>
                        <strong>Summary:</strong> {detection.aiAnalysis.summary}
                      </Typography>
                      
                      {detection.aiAnalysis.htmlSummary && (
                        <Box
                          dangerouslySetInnerHTML={{
                            __html: detection.aiAnalysis.htmlSummary
                          }}
                          sx={{
                            '& .detection-summary': {
                              '& .alert': {
                                padding: '12px',
                                borderRadius: '4px',
                                marginBottom: '16px',
                              },
                              '& .alert-danger': {
                                backgroundColor: '#ffebee',
                                borderLeft: '4px solid #f44336',
                              },
                              '& .alert-warning': {
                                backgroundColor: '#fff3e0',
                                borderLeft: '4px solid #ff9800',
                              },
                              '& .badge': {
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                marginRight: '8px',
                              },
                              '& .badge-danger': {
                                backgroundColor: '#f44336',
                                color: 'white',
                              },
                              '& .badge-warning': {
                                backgroundColor: '#ff9800',
                                color: 'white',
                              },
                              '& .badge-outline-primary': {
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                backgroundColor: 'transparent',
                              },
                            }
                          }}
                        />
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Enrichment Data */}
              {detection.enrichmentData && (
                <Accordion sx={{ mb: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Enrichment Data</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {/* Endpoint Information */}
                      {detection.enrichmentData.endpointInfo && (
                        <Box mb={3}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Endpoint Information
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2">
                                <strong>Hostname:</strong> {detection.enrichmentData.endpointInfo.hostname || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>IP Address:</strong> {detection.enrichmentData.endpointInfo.ipAddress || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>MAC Address:</strong> {detection.enrichmentData.endpointInfo.macAddress || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Operating System:</strong> {detection.enrichmentData.endpointInfo.operatingSystem || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2">
                                <strong>Status:</strong> {detection.enrichmentData.endpointInfo.status || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Risk Score:</strong> {detection.enrichmentData.endpointInfo.riskScore || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Last Seen:</strong> {detection.enrichmentData.endpointInfo.lastSeen ?
                                  new Date(detection.enrichmentData.endpointInfo.lastSeen).toLocaleString() : 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>

                          {/* Microsoft Defender Specific Data */}
                          {detection.enrichmentData.endpointInfo.defenderData && (
                            <Box mt={2}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Microsoft Defender Details
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2">
                                    <strong>Agent Version:</strong> {detection.enrichmentData.endpointInfo.defenderData.agentVersion || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Health Status:</strong> {detection.enrichmentData.endpointInfo.defenderData.healthStatus || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Onboarding Status:</strong> {detection.enrichmentData.endpointInfo.defenderData.onboardingStatus || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2">
                                    <strong>Defender Risk Score:</strong> {detection.enrichmentData.endpointInfo.defenderData.riskScore || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Exposure Level:</strong> {detection.enrichmentData.endpointInfo.defenderData.exposureLevel || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Machine ID:</strong> {detection.enrichmentData.endpointInfo.defenderData.machineId || 'N/A'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Indicators */}
                      {detection.enrichmentData.indicators && detection.enrichmentData.indicators.length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Extracted Indicators ({detection.enrichmentData.indicators.length})
                          </Typography>
                          <Box>
                            {detection.enrichmentData.indicators.map((indicator, index) => (
                              <Box key={index} mb={1}>
                                <Chip
                                  label={`${indicator.type}: ${indicator.value}`}
                                  variant="outlined"
                                  size="small"
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Threat Intelligence Matches */}
                      {detection.enrichmentData.threatIntelMatches && detection.enrichmentData.threatIntelMatches.length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Threat Intelligence Matches ({detection.enrichmentData.threatIntelMatches.length})
                          </Typography>
                          <Box>
                            {detection.enrichmentData.threatIntelMatches.map((match, index) => (
                              <Box key={index} mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
                                <Typography variant="body2">
                                  <strong>Indicator:</strong> {match.indicator}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Threat Type:</strong> {match.threatType}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Severity:</strong> {match.severity}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Confidence:</strong> {match.confidence}%
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Additional Enrichment Data */}
                      {detection.enrichmentData.extractedData && Object.keys(detection.enrichmentData.extractedData).length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Additional Enrichment Data
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              backgroundColor: 'grey.100',
                              p: 2,
                              borderRadius: 1,
                              overflow: 'auto',
                              fontSize: '0.875rem',
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {JSON.stringify(detection.enrichmentData.extractedData, null, 2)}
                          </Box>
                        </Box>
                      )}

                      {/* No Enrichment Data Message */}
                      {(!detection.enrichmentData.endpointInfo &&
                        (!detection.enrichmentData.indicators || detection.enrichmentData.indicators.length === 0) &&
                        (!detection.enrichmentData.threatIntelMatches || detection.enrichmentData.threatIntelMatches.length === 0) &&
                        (!detection.enrichmentData.extractedData || Object.keys(detection.enrichmentData.extractedData).length === 0)) && (
                        <Typography variant="body2" color="textSecondary">
                          No enrichment data available for this detection.
                        </Typography>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Raw Data */}
              {detection.rawData && (
                <Accordion sx={{ mb: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Raw Alert Data</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {detection.rawData}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Tags */}
              {detection.tags && detection.tags.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Tags
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {detection.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Timeline */}
              {detection.timeline && detection.timeline.length > 0 && (
                <Accordion sx={{ mb: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TimelineIcon />
                      <Typography variant="h6">Timeline</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {detection.timeline.map((event, index) => (
                        <ListItem key={index} divider={index < detection.timeline.length - 1}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={event.action}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="textSecondary">
                                  {event.description}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {formatDetectionDateTime(event.timestamp)} by {event.isSystemGenerated || !event.performedBy ? 'System' : `${event.performedBy.firstName || ''} ${event.performedBy.lastName || ''}`.trim() || 'System'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Comments Section */}
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <CommentIcon />
                    <Typography variant="h6">Comments</Typography>
                  </Box>

                  {/* Existing Comments */}
                  {detection.comments && detection.comments.length > 0 ? (
                    <List sx={{ mb: 2 }}>
                      {detection.comments.map((comment, index) => (
                        <ListItem key={index} divider={index < detection.comments.length - 1}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                              {comment.author?.firstName?.[0]}{comment.author?.lastName?.[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={comment.content}
                            secondary={
                              <Typography variant="caption" color="textSecondary">
                                {formatDateTime(comment.createdAt)} by {comment.author ? `${comment.author.firstName} ${comment.author.lastName}` : 'Unknown'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      No comments yet. Be the first to add a comment.
                    </Typography>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  {/* Add New Comment */}
                  <Box display="flex" gap={1}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      disabled={addingComment}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || addingComment}
                      startIcon={addingComment ? <CircularProgress size={16} /> : <SendIcon />}
                      sx={{ minWidth: 100 }}
                    >
                      {addingComment ? 'Adding...' : 'Add'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {/* Quick Actions */}
        {detection && (
          <>
            {detection.status === 'New' && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSaveCaseManagement({ status: 'In Progress' })}
                disabled={savingCaseManagement}
              >
                Start Investigation
              </Button>
            )}

            {detection.status === 'In Progress' && (
              <Button
                variant="contained"
                color="success"
                onClick={() => handleSaveCaseManagement({ status: 'Resolved' })}
                disabled={savingCaseManagement}
              >
                Mark Resolved
              </Button>
            )}

            {user?._id && detection.assignedTo?._id !== user._id && (
              <Button
                variant="outlined"
                startIcon={<PersonIcon />}
                onClick={() => handleSaveCaseManagement({ assignedTo: user._id })}
                disabled={savingCaseManagement}
              >
                Assign to Me
              </Button>
            )}
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetectionDetail;
